import React, { useState, useMemo, useCallback } from "react";
import {
  TableCompData,
  CardComp,
  ButtonComp,
  MoneyInputComp,
} from "../../../components";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { BsArrowLeft } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import EmptyTable from "../../../components/Common/EmptyTable";
import { formatMoney } from "../../../utils";
import moment from "moment";
import PageWrapper from "../modules/PageWrapper";
import MemberPendingBankTransfersCard from "../modules/MemberPendingBankTransfersCard";
import MemberPaymentModeChooser from "../modules/MemberPaymentModeChooser";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import {
  usePayMemberInformalLoanMutation,
  usePayMemberInformalLoanBankTransferMutation,
} from "../../../store/member/member-loan";
import { useGetMemberCompanyBanksQuery } from "../../../store/member/member-contribution";
import toast from "react-hot-toast";
import {
  getFrontendBaseUrl,
  isPaystackMode,
  isSocietyBankMode,
  redirectToMemberOnlinePayment,
} from "../../../utils/memberOnlinePayment";

/**
 * Informal (flexible) member loan repayment breakdown.
 * Shows principal, rate, next interest application date; current principal, accrued interest, total balance;
 * inline pay block (amount + Pay with Paystack), and payment history.
 */
const InformalMemberLoanRepaymentBreakdown = ({
  data,
  isLoading,
  loanRequestId,
}) => {
  const navigate = useNavigate();
  const [payInformal, { isLoading: isPaying }] = usePayMemberInformalLoanMutation();
  const [payInformalBank, { isLoading: isPayingBank }] =
    usePayMemberInformalLoanBankTransferMutation();
  const { data: companyBanks = [] } = useGetMemberCompanyBanksQuery();
  const [paymentMode, setPaymentMode] = useState("paystack");
  const [selectedBankId, setSelectedBankId] = useState("");
  const [memberNote, setMemberNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePaymentModeChange = useCallback((mode) => {
    setPaymentMode(mode);
    if (isSocietyBankMode(mode)) {
      return;
    }
    setSelectedBankId("");
    setMemberNote("");
  }, []);

  const currentBalance = Number(
    data?.total_balance ?? data?.outstanding ?? data?.remaining_balance ?? 0
  );
  const isLoanCompleted =
    data?.status === "PAID" || currentBalance === 0;

  const payValidation = React.useMemo(
    () =>
      Yup.object({
        amount: Yup.string()
          .required("Required")
          .test(
            "maxBalance",
            `Amount cannot exceed current balance (${currentBalance > 0 ? formatMoney(currentBalance.toFixed(2)) : "—"})`,
            (value) => {
              if (currentBalance <= 0) return true;
              const num = Number(value?.replace?.(/,/g, "") ?? value);
              if (Number.isNaN(num) || num <= 0) return true;
              return num <= currentBalance;
            }
          ),
      }),
    [currentBalance]
  );

  const selectedBank = useMemo(
    () =>
      (companyBanks || []).find(
        (b) => String(b?.id) === String(selectedBankId)
      ),
    [companyBanks, selectedBankId]
  );

  const handlePay = async (values) => {
    if (isSubmitting || isPaying || isPayingBank) return;
    const amount = Number(String(values.amount).replace(/,/g, ""));
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (amount > currentBalance) {
      toast.error("Amount cannot exceed current balance");
      return;
    }
    if (isSocietyBankMode(paymentMode) && !selectedBankId) {
      toast.error("Please select the society account you paid into");
      return;
    }
    setIsSubmitting(true);
    try {
      if (isSocietyBankMode(paymentMode)) {
        const result = await payInformalBank({
          loan_request_id: Number(loanRequestId),
          amount: String(amount),
          company_bank_id: parseInt(selectedBankId, 10),
          member_note: memberNote?.trim() || undefined,
        }).unwrap();
        if (!result?.status) {
          toast.error(result?.message || "Request failed");
          return;
        }
        toast.success(
          "Transfer request recorded. Pay the exact amount entered; staff will confirm after verifying your deposit."
        );
        setSelectedBankId("");
        setMemberNote("");
        return;
      }
      const response = await payInformal({
        loan_request_id: Number(loanRequestId),
        amount: String(amount),
        payment_provider: isPaystackMode(paymentMode) ? "paystack" : "alatpay",
        frontend_base_url: getFrontendBaseUrl(),
      }).unwrap();
      if (response?.data?.payment_url || response?.data?.authorization_url) {
        const breakdown = response?.data?.fee_breakdown;
        const totalToPay = breakdown?.member_payable_total ?? amount;
        toast.success(`Redirecting to payment (Total: ${formatMoney(Number(totalToPay).toFixed(2))})...`);
        if (!redirectToMemberOnlinePayment(response)) {
          toast.error("Payment URL not received. Please try again.");
        }
      } else {
        toast.error(response?.message || "Failed to start payment");
      }
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Failed to start payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const paymentHistory = data?.payment_history ?? data?.payments ?? [];
  const columns = [
    {
      name: "S/N",
      selector: (_, index) => (
        <span className="text-muted fw-2" style={{ fontSize: "14px" }}>
          {index + 1}
        </span>
      ),
      width: "60px",
    },
    {
      name: "DATE",
      selector: (row) => (
        <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
          {row?.paid_at
            ? moment(row.paid_at).format("DD-MMM-YYYY")
            : row?.payment_date
            ? moment(row.payment_date).format("DD-MMM-YYYY")
            : "—"}
        </span>
      ),
      minWidth: "140px",
    },
    {
      name: "AMOUNT",
      selector: (row) => {
        const amount = parseFloat(row?.amount ?? 0);
        return (
          <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
            {formatMoney(amount.toFixed(2))}
          </span>
        );
      },
      minWidth: "160px",
    },
    {
      name: "BALANCE AFTER",
      selector: (row) => (
        <span className="fw-2 text-muted" style={{ fontSize: "14px" }}>
          {row?.balance_after != null
            ? formatMoney(Number(row.balance_after).toFixed(2))
            : "—"}
        </span>
      ),
      minWidth: "140px",
    },
  ];

  return (
    <PageWrapper title="Loan Repayment Breakdown" back={false}>
      <section className="px-lg-3 min-vh-100 mb-3">
        <div className="space-y-4">
          <MemberPendingBankTransfersCard flowFilter="loan" />
          <CardComp cardClassName="border-0 shadow-sm mb-4">
            <div className="d-flex align-items-center gap-3 p-3">
              <BsArrowLeft
                size={24}
                onClick={() => navigate(-1)}
                className="cursor-pointer text-primary1"
              />
              <div className="flex-grow-1">
                <h4 className="fw-bold mb-1 text-primary1">
                  {data?.loan_type?.name || "Loan"} (Flexible)
                </h4>
              </div>
            </div>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm mb-4">
            <div className="p-3">
              <p className="text-muted mb-2 small">
                Interest is applied monthly on the meeting date as{" "}
                <strong>
                  {Number(data?.loan_type?.rate ?? data?.rate ?? 0).toFixed(1)}%
                </strong>{" "}
                of your current balance.
              </p>
              <p className="text-muted mb-3 small">
                Next interest application date:{" "}
                <strong>
                  {data?.next_interest_application_date
                    ? moment(data.next_interest_application_date).format("DD-MMM-YYYY")
                    : data?.next_evaluation_date
                    ? moment(data.next_evaluation_date).format("DD-MMM-YYYY")
                    : "—"}
                </strong>
              </p>
            </div>
            <div className="row g-4 p-3 pt-0">
              <div className="col-md-3 col-6">
                <div className="text-center">
                  <p className="text-muted mb-2 small fw-semibold">Principal</p>
                  <span className="text-3xl fw-bold text-dark">
                    {formatMoney(
                      Number(data?.principal ?? data?.loan_amount ?? data?.total_payable ?? 0).toFixed(2)
                    )}
                  </span>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                  <p className="text-muted mb-2 small fw-semibold">Current principal</p>
                  <span className="text-3xl fw-bold text-dark">
                    {formatMoney(
                      Number(data?.current_principal ?? data?.principal ?? data?.loan_amount ?? 0).toFixed(2)
                    )}
                  </span>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                  <p className="text-muted mb-2 small fw-semibold">Accrued interest</p>
                  <span className="text-3xl fw-bold text-secondary">
                    {formatMoney(
                      Number(data?.accrued_interest ?? 0).toFixed(2)
                    )}
                  </span>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                  <p className="text-muted mb-2 small fw-semibold">Total balance</p>
                  <span className="text-3xl fw-bold text-danger">
                    {formatMoney(
                      Number(data?.total_balance ?? data?.outstanding ?? data?.remaining_balance ?? 0).toFixed(2)
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className="row g-4 p-3 pt-0">
              <div className="col-md-4 col-6">
                <p className="text-muted mb-1 small fw-semibold">Total paid</p>
                <span className="fw-bold text-success">
                  {formatMoney(Number(data?.total_paid ?? 0).toFixed(2))}
                </span>
              </div>
            </div>
          </CardComp>

          {!isLoanCompleted && (
            <CardComp cardClassName="border-0 shadow-sm mb-4">
              <div className="mb-3 px-3">
                <h5 className="fw-bold mb-1 text-primary1">Pay</h5>
                <p className="text-muted mb-2 small">
                  Enter an amount up to your current balance, then choose how you
                  want to pay.
                </p>
                {currentBalance > 0 && (
                  <p className="text-black1 mb-0 mt-1 small">
                    Current balance: <strong>{formatMoney(currentBalance.toFixed(2))}</strong>
                  </p>
                )}
                <div className="mt-3">
                  <MemberPaymentModeChooser
                    paymentMode={paymentMode}
                    onPaymentModeChange={handlePaymentModeChange}
                    companyBanks={companyBanks}
                    selectedBankId={selectedBankId}
                    onSelectedBankIdChange={setSelectedBankId}
                    memberNote={memberNote}
                    onMemberNoteChange={setMemberNote}
                    compact
                  />
                  {isSocietyBankMode(paymentMode) && selectedBank && (
                    <div className="p-2 mt-2 rounded small" style={{ backgroundColor: "#f7f7f7" }}>
                      <p className="mb-1 fw-semibold">Pay into</p>
                      <p className="mb-0">{selectedBank.bank_name}</p>
                      <p className="mb-0">{selectedBank.account_name}</p>
                      <p className="mb-0 fw-semibold">{selectedBank.account_number}</p>
                    </div>
                  )}
                </div>
              </div>
              <Formik
                initialValues={{ amount: "" }}
                validationSchema={payValidation}
                onSubmit={handlePay}
              >
                {({ errors, touched }) => (
                  <Form className="px-3 pb-3 pt-0">
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <MoneyInputComp
                          LabelText="Amount"
                          borderColor="#A1A0A0"
                          LabelClassName="fw-3"
                          placeholder="Enter amount"
                          name="amount"
                        />
                        {touched.amount && errors.amount && (
                          <small className="text-danger d-block mt-1">{errors.amount}</small>
                        )}
                      </div>
                      <div className="col-12 mt-2">
                        <ButtonComp
                          type="submit"
                          btnText={
                            isSocietyBankMode(paymentMode)
                              ? "Submit transfer request"
                              : isPaystackMode(paymentMode)
                              ? "Pay with card"
                              : "Pay with ALATPay"
                          }
                          btnClassName="bg-primary1 px-4 py-2 text-white rounded border-0"
                          loading={isSocietyBankMode(paymentMode) ? isPayingBank : isPaying}
                          disabled={
                            isSubmitting ||
                            (isSocietyBankMode(paymentMode) && !selectedBankId)
                          }
                          style={{ minHeight: "44px", width: "auto", minWidth: "200px" }}
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </CardComp>
          )}

          {isLoanCompleted && (
            <CardComp cardClassName="border-0 shadow-sm mb-4">
              <div className="p-3">
                <h5 className="fw-bold mb-1 text-success">Completed</h5>
                <p className="text-muted mb-0 small">
                  This loan has been fully repaid. No further payments are required.
                </p>
              </div>
            </CardComp>
          )}

          <CardComp cardClassName="border-0 shadow-sm">
            <div className="mb-4 p-3">
              <h5 className="fw-bold mb-1 text-primary1">Payment History</h5>
              <p className="text-muted mb-0 small">
                All payments recorded for this loan
              </p>
            </div>
            <TableCompData
              marginBottom="1rem"
              columns={columns}
              data={paymentHistory}
              progressPending={isLoading}
              progressComponent={<TableSkeletonLoader rows={8} columns={4} />}
              message={
                !isLoading && (
                  <EmptyTable Message="No payments recorded yet" />
                )
              }
              cellPadding="12px"
            />
          </CardComp>
        </div>
      </section>
    </PageWrapper>
  );
};

export default InformalMemberLoanRepaymentBreakdown;
